import React from 'react';
import {
  VictoryChart,
  VictoryLine,
  VictoryAxis,
  VictoryTheme,
  VictoryLegend,
  VictoryScatter,
  VictoryLabel,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';
import { formatPeriodES } from '../../../config/reportTranslations';
import './Charts.css';
import { REPORT_CHART_COLORS } from '../../../constants/colors';

const COLORS = REPORT_CHART_COLORS;

/**
 * Gráfico 08: Liquidez General
 *
 * Line chart showing:
 * - COAC liquidity ratio (teal line)
 * - S1 segment benchmark (dark gray line, optional)
 *
 * Matches PDF report format from Atuntaqui_mar2025
 */

export interface Grafico08V2Data {
  period: string;              // YYYY-MM format
  liquidez_coac: number;       // Institution liquidity (e.g., 0.3585 = 35.85%)
  liquidez_s1?: number;        // Segment benchmark (optional)
}

export interface Grafico08V2Props {
  data: Grafico08V2Data[];
  title?: string;
}

export const Grafico08V2: React.FC<Grafico08V2Props> = ({
  data,
  title = 'Gráfico 08: Liquidez General'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Sort data by period (oldest first)
  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Check if we have benchmark data
  const hasBenchmark = sortedData.some(d => d.liquidez_s1 !== undefined);

  // COAC data - line and scatter
  const coacLineData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.liquidez_coac * 100,
  }));

  const coacScatterData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.liquidez_coac * 100,
    label: `${(d.liquidez_coac * 100).toFixed(2)}%`
  }));

  // S1 benchmark data - line and scatter
  const s1LineData = hasBenchmark
    ? sortedData.map(d => ({
        x: formatPeriodES(d.period),
        y: (d.liquidez_s1 || 0) * 100,
      }))
    : [];

  const s1ScatterData = hasBenchmark
    ? sortedData.map(d => ({
        x: formatPeriodES(d.period),
        y: (d.liquidez_s1 || 0) * 100,
        label: `${((d.liquidez_s1 || 0) * 100).toFixed(2)}%`
      }))
    : [];

  // Calculate y-axis domain
  const allValues = [...coacLineData.map(d => d.y), ...s1LineData.map(d => d.y)];
  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...allValues);

  return (
    <div className="chart-container">
      <h4 className="chart-title">{title}</h4>

      <div className="chart-wrapper">
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={{ x: 30, y: 10 }}
          padding={{ top: 50, bottom: 60, left: 55, right: 20 }}
          height={280}
          containerComponent={
            <VictoryVoronoiContainer
              labels={({ datum }) => datum.label}
              labelComponent={<VictoryTooltip style={{ fontSize: 9 }} />}
            />
          }
        >
          {/* Legend */}
          <VictoryLegend
            x={100}
            y={5}
            orientation="horizontal"
            gutter={30}
            style={{ labels: { fontSize: 9 } }}
            data={[
              { name: 'Liquidez COAC', symbol: { fill: COLORS.secondary, type: 'minus' } },
              ...(hasBenchmark ? [{ name: 'Liquidez (S1)', symbol: { fill: COLORS.primary, type: 'minus' } }] : [])
            ]}
          />

          {/* X-Axis */}
          <VictoryAxis
            style={{
              axis: { stroke: '#666' },
              tickLabels: {
                fontSize: 9,
                padding: 5,
                angle: -45,
                textAnchor: 'end'
              }
            }}
          />

          {/* Y-Axis */}
          <VictoryAxis
            dependentAxis
            domain={[Math.max(0, minValue * 0.8), maxValue * 1.15]}
            tickFormat={(t: number) => `${t.toFixed(0)}%`}
            style={{
              axis: { stroke: '#666' },
              tickLabels: { fontSize: 9, padding: 5 },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* S1 Benchmark Line (if available) - dark gray */}
          {hasBenchmark && (
            <>
              <VictoryLine
                data={s1LineData}
                style={{
                  data: {
                    stroke: COLORS.primary,
                    strokeWidth: 1.2
                  }
                }}
              />
              <VictoryScatter
                data={s1ScatterData}
                size={3}
                style={{ data: { fill: COLORS.primary } }}
              />
            </>
          )}

          {/* COAC Line - teal */}
          <VictoryLine
            data={coacLineData}
            style={{
              data: {
                stroke: COLORS.secondary,
                strokeWidth: 1.2
              }
            }}
          />

          {/* COAC Points with value labels */}
          <VictoryScatter
            data={coacScatterData}
            size={3}
            style={{ data: { fill: COLORS.secondary } }}
            labelComponent={<VictoryLabel dy={-10} style={{ fontSize: 8, fill: COLORS.secondary }} />}
          />
        </VictoryChart>
      </div>

      <p className="chart-source">
        Fuente: COAC / Elaboración: UnionRatings
      </p>
    </div>
  );
};

export default Grafico08V2;
